/**
 * Activity Feed Types
 * Matches backend DTOs from ActivityController
 */
import { SetDetail } from 'store/services/trade/tradeApi';

export interface ActivityUser {
  id: number;
  username: string;
  avatarUrl: string | null;
  countryCode: string | null;
  /** Raw country column as exposed by other endpoints (e.g. /public/profile returns
   *  `country: "US"`). Accepted as a fallback for `countryCode` so the avatar flag renders
   *  whichever name the backend activity DTO uses. See getActivityFeed transformResponse. */
  country?: string | null;
  points: number | null;
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
}

export interface ActivityCard {
  id: number;
  name: string;
  scryfallId: string;
  imageUrl: string; // Full CDN URL from backend - use directly
  setCode: string;
  collectorNumber: string | null;
  foil: boolean;
  /**
   * Wanter acceptance criteria — what printing/finish/language/condition the requester will accept.
   * Present on want_added + available items only (the backend enriches these via the shared
   * acceptance module Trade Alerts uses). All optional → the flyout renders defensively
   * ("Any set / Any finish / Any language" when absent).
   */
  acceptableSetDetails?: SetDetail[];
  acceptableConditions?: string[];
  acceptableLanguages?: string[];
  acceptFoil?: boolean;
  acceptNonFoil?: boolean;
  availableInFoil?: boolean;
  availableInNonFoil?: boolean;
  availableLanguages?: Array<{ lang: string; scryfallId: string; setCode?: string }>;
}

export type ActivityType = 'trade_sent' | 'trade_received' | 'want_added' | 'card_added' | 'trade_shipped' | 'available';

export interface Activity {
  id: string; // Format: ${type}_${sourceId}_${itemIndex}
  type: ActivityType;
  createdAt: string; // ISO timestamp
  user: ActivityUser;
  card: ActivityCard;
  pointValue: number | null; // null → show "???"
  wantEntryId?: number; // Present on want_added and available items, used for dedup
}

export interface RecentActivityResponse {
  activities: Activity[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface RecentActivityRequest {
  limit?: number;
  cursor?: string;
  /** Inclusive lower/upper bound on the acting trader's current point balance. */
  traderBalanceMin?: number;
  traderBalanceMax?: number;
}

/**
 * Activity Stats - Live Pulse Dashboard
 * Matches backend ActivityStatsResponse from GET /activity/stats
 */
export interface ActivityStats {
  tradesLastHour: number;
  packagesShippedToday: number;
  cardsTradedThisWeek: number;
}

/**
 * Get display text for activity type
 */
export const getActivityActionText = (type: ActivityType): string => {
  switch (type) {
    case 'trade_sent':
      return 'sent:';
    case 'trade_received':
      return 'received:';
    case 'want_added':
      return 'wants:';
    case 'card_added':
      return 'added:';
    case 'trade_shipped':
      return 'shipped:';
    case 'available':
      return 'available:';
    default:
      return '';
  }
};

/**
 * Available Feed Types
 * For GET /api/v1/activity/available endpoint
 */
export interface AvailableFeedRequest {
  limit?: number;
  hours?: number;
}

export interface AvailableFeedItem {
  wantEntryId: number;
  type: 'available';
  timestamp: string;
  card: {
    id: number;
    name: string;
    setName: string;
    setCode: string;
    collectorNumber: string;
    scryfallId: string;
    imageUrl: string;
    pointValue: number;
  };
  user: {
    id: number;
    username: string;
    avatarUrl: string | null;
    country: string | null;
    countryCode: string | null;
    points: number | null;
    isElite?: boolean;
    showAnimatedBorder?: boolean;
    borderColor1?: string | null;
    borderColor2?: string | null;
  };
}

export interface AvailableFeedResponse {
  items: AvailableFeedItem[];
  totalItems: number;
  lookbackHours: number;
}
